import type { DomainCommand } from '../protocol/commands/domain-command';
import { chatEvents, errorEvents } from '..';
import type { DomainEvent } from '../protocol/events/domain-event';
import { UUIDGenerator } from '../utils/uuid-generator';
import { merge, Observable, throwError, of } from 'rxjs';
import { filter, map, mergeMap, take, timeout, ignoreElements, tap } from 'rxjs/operators';
import { roomCommand } from '../protocol/commands/room-commands';
import { Socket } from 'socket.io-client';

export class SocketIOClient {

  private socket$: Observable<unknown>;

  constructor(
    private io: Socket,
    private uuidGenerator: UUIDGenerator,
    private askTimeoutMs: number,
  ) {
    this.socket$ = new Observable((observer) => {
      io.on("message", (ev) => {
        observer.next(JSON.parse(ev));
      });
      return {
        unsubscribe: io.close,
      }
    });
  }

  public get connection$(): Observable<DomainEvent> {
    return this.socket$.pipe(
      map(ev => ev as DomainEvent),
    );
  }

  public send(command: DomainCommand): void {
    this.io.send("message", JSON.stringify(command));
  }

  /**
   * Cold observable
   * @param command Message
   */
  public ask(command: roomCommand.SendMessage | roomCommand.SendCustomMessage): Observable<chatEvents.Received> {
    const ref = this.uuidGenerator.next();
    const newCommand = { ...command, ref };

    return merge(
      of(newCommand).pipe(
        tap((cmd: typeof newCommand) => this.send(cmd)),
        ignoreElements(),
      ),
      this.connection$.pipe(
        filter(chatEvents.Received.isReceived),
        filter(rec => rec.ref === ref),
      ),
      this.connection$.pipe(
        filter(errorEvents.Error.isError),
        filter(rec => rec.ref === ref),
        mergeMap(err => throwError(err)),
      ),
    ).pipe(
      timeout(this.askTimeoutMs),
      take(1),
    );
  }
}
